import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type MatchUnit = "Lines" | "Bills";

function isoDaysAgo(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Inclusive day count of the default window: today plus the 29 days before it. */
const DEFAULT_WINDOW_DAYS = 30;

/** Every filter lives in the URL so a view is linkable. One window governs the whole page; default is the
 * last 30 days in UTC. */
export function useAnalyticsParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const clientIds = useMemo(() => searchParams.getAll("client"), [searchParams]);
  const from = searchParams.get("from") ?? isoDaysAgo(DEFAULT_WINDOW_DAYS - 1);
  const to = searchParams.get("to") ?? today();
  const organisationId = searchParams.get("org") ?? undefined;
  // Bills by default: the number usually asked about. Bill items (one per matched line) is the engineer's number.
  const unit = (searchParams.get("unit") as MatchUnit | null) ?? "Bills";

  const setParam = useCallback(
    (key: string, value: string | string[] | undefined) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(key);
          if (Array.isArray(value)) {
            for (const entry of value) next.append(key, entry);
          } else if (value) {
            next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /** The query string the backend expects, shared by every section and every CSV link. */
  const apiParams = useMemo(() => {
    const params = new URLSearchParams();
    for (const id of clientIds) params.append("ClientIds", id);
    params.set("From", from);
    params.set("To", to);
    if (organisationId) params.set("OrganisationId", organisationId);
    return params;
  }, [clientIds, from, to, organisationId]);

  return {
    clientIds,
    from,
    to,
    organisationId,
    unit,
    setParam,
    apiParams,
    ready: clientIds.length > 0,
  };
}
